import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { ArrowRight, Storefront, Backspace, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import HomePage from "./HomePage";
import { useTheme } from "../ThemeContext";
import pkg from "../../package.json";
import InstallAppPrompt from "../components/InstallAppPrompt";

const APP_VERSION = pkg.version;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Store splash shown for ~1.8s right after a successful login. Logo pulses 3x,
// "Hey, {first_name}!" rises in, then "Welcome to Store {N} – {City}", the
// whole overlay fades out, then we hand off to the HomePage. Personal greeting
// added 2026-02-22 per user ask "add their name in the splash like Hey Dom".
function StoreSplash({ logoUrl, storeNumber, storeCity, firstName, onDone }) {
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setLeaving(true), 1550);
    const t2 = setTimeout(onDone, 1900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);
  return (
    <div
      className={`fixed inset-0 z-[60] flex flex-col items-center justify-center text-white ${leaving ? "fb-splash-leave" : ""}`}
      style={{ background: "linear-gradient(160deg, var(--fb-blue-dark) 0%, var(--fb-blue) 45%, #1E40AF 100%)" }}
      data-testid="store-splash"
      aria-live="polite"
    >
      <div className="fb-splash-pulse">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="w-24 h-24 rounded-2xl object-cover shadow-2xl ring-4 ring-white/20" />
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-white/10 ring-4 ring-white/20 flex items-center justify-center">
            <Storefront weight="fill" className="w-12 h-12 text-white" />
          </div>
        )}
      </div>
      {firstName && (
        <h1
          className="fb-splash-text mt-6 text-4xl sm:text-5xl font-black tracking-tight text-center px-6"
          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.02em" }}
          data-testid="store-splash-greeting"
        >
          Hey, {firstName}!
        </h1>
      )}
      <p className="fb-splash-text mt-4 text-[10px] uppercase font-black tracking-[0.3em] text-white/70">Welcome to</p>
      <h2
        className="fb-splash-text mt-1 text-2xl sm:text-3xl font-black tracking-tight text-center px-6"
        style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
        data-testid="store-splash-title"
      >
        Store {storeNumber || "3094"}
      </h2>
      <p
        className="fb-splash-text mt-1 text-base sm:text-lg font-semibold text-white/90"
        style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
        data-testid="store-splash-city"
      >
        {storeCity || "Wheat Ridge"}
      </p>
    </div>
  );
}

// PIN entry screen — replaces the Employee ID card after Step 1 succeeds.
// Renders 4 large dot-slots that fill as the user types on a custom 0-9 keypad.
// Custom keypad (vs. an <input>) so iOS Safari doesn't show its number-row UI
// or shift the layout when the keyboard pops — this matches the user's ask
// "I don't want an ugly pin box under the employee ID". The same component
// handles both confirm-existing-PIN and first-time-setup (with a "Confirm PIN"
// second pass when setting up).
function PinPad({ length, value, onChange }) {
  // Render `length` rounded dot-slots; filled slots get a solid blue dot.
  // Slot sizing scales down on tight screens (Zebra scanners — ~480px tall
  // landscape, ~360px portrait) so the whole pin card + keypad fits without
  // overflow. (User report 2026-05-26: "pin pad doesn't fit on our work
  // zebras".) sm: breakpoint restores the larger comfortable size.
  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" role="group" aria-label="PIN entry">
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          className={`w-10 h-12 sm:w-12 sm:h-14 rounded-2xl border-2 flex items-center justify-center transition-colors duration-100 ${
            value.length === i ? "border-blue-500 ring-4 ring-blue-100" : "border-slate-200"
          }`}
          data-testid={`pin-slot-${i}`}
        >
          {value[i] && <span className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-blue-600" />}
        </div>
      ))}
      {/* Hidden mirror so screen readers can announce progress. */}
      <span className="sr-only">{value.length} of {length} digits entered</span>
      {/* Suppress unused-var warning for onChange — used by the keypad below. */}
      <span hidden>{typeof onChange === "function" ? "" : ""}</span>
    </div>
  );
}

function Keypad({ onDigit, onBackspace, disabled }) {
  // 3×4 grid (1-9, blank, 0, ⌫). Big touch targets so the Zebra scanners and
  // older crew still hit the right key. Active state uses scale-95 + ring-blue
  // so it feels tactile even with no haptic feedback on the Zebra.
  //
  // Heights scale: h-11 on tight Zebra screens, h-14 at sm+. Gap also
  // tightens. Buttons stay above the 44pt iOS HIG min-target. (Fix for
  // user report 2026-05-26 "pin pad doesn't fit on our work zebras".)
  const Btn = ({ children, onClick, testId, ariaLabel }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-11 sm:h-14 rounded-2xl bg-slate-100 hover:bg-slate-200 active:bg-blue-100 active:scale-95 transition-transform duration-75 text-xl sm:text-2xl font-black text-slate-800 disabled:opacity-40 disabled:active:scale-100"
      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', Inter, sans-serif" }}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {children}
    </button>
  );
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-2.5 mt-3 sm:mt-4">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
        <Btn key={n} onClick={() => onDigit(String(n))} testId={`keypad-${n}`} ariaLabel={`Digit ${n}`}>{n}</Btn>
      ))}
      <span /> {/* spacer */}
      <Btn onClick={() => onDigit("0")} testId="keypad-0" ariaLabel="Digit 0">0</Btn>
      <Btn onClick={onBackspace} testId="keypad-backspace" ariaLabel="Backspace">
        <Backspace weight="bold" className="w-5 h-5 sm:w-6 sm:h-6 mx-auto" />
      </Btn>
    </div>
  );
}

export default function LoginPage({ onLogin, crew = [], maintenanceOn = false, pauseBannerMessage = "" }) {
  // ---- Login flow state machine -----------------------------------------
  //   "id"          → user types Employee ID (Step 1)
  //   "pin"         → user types existing PIN (Step 2a)
  //   "pin-setup"   → user picks new PIN (Step 2b — first time only)
  //   "pin-confirm" → user re-types new PIN to confirm (Step 2b cont.)
  // ----------------------------------------------------------------------
  const [step, setStep] = useState(() => {
    // 2026-08-31 soft-lock recovery: if App.js's silent-refresh hop wrote
    // `ecDashSoftLock` before punting here, jump straight to the PIN pad
    // instead of asking for the Employee ID again. Kills the "sign in
    // twice a day" friction for shared-iPad users while keeping full
    // auth semantics (they still have to type their PIN).
    try {
      const sl = localStorage.getItem("ecDashSoftLock");
      if (sl && JSON.parse(sl)?.employee_id) return "pin";
    } catch { /* ignore */ }
    return "id";
  });
  const [employeeId, setEmployeeId] = useState(() => {
    try {
      const sl = localStorage.getItem("ecDashSoftLock");
      if (sl) return JSON.parse(sl)?.employee_id || "";
    } catch { /* ignore */ }
    return "";
  });
  const [pendingFirstName, setPendingFirstName] = useState(() => {
    try {
      const sl = localStorage.getItem("ecDashSoftLock");
      if (sl) return JSON.parse(sl)?.first_name || "";
    } catch { /* ignore */ }
    return "";
  });
  const [pin, setPin] = useState("");
  const [pinSetupFirst, setPinSetupFirst] = useState(""); // first pass in setup mode
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [splashDone, setSplashDone] = useState(false);
  const [storeInfo, setStoreInfo] = useState({ store_number: "3094", store_city: "Wheat Ridge" });
  // Pre-PIN context surfaced by /auth/crew-login (photo, last login,
  // whether the crew already has a passkey on this device). (User asks
  // 2026-05-25: b. personalized greeting · e. avatar on PIN screen ·
  // f. Face ID.)
  const [pendingCtx, setPendingCtx] = useState(() => {
    // Pre-populate from soft-lock so the PIN screen shows the crew's
    // avatar the instant the app opens (no /auth/crew-login round trip
    // needed to fill in the greeting).
    try {
      const sl = localStorage.getItem("ecDashSoftLock");
      if (sl) {
        const s = JSON.parse(sl);
        return {
          photo_url: s.avatar_url || "",
          last_login_at: "",
          has_webauthn: false,
          role_label: "",
          position: "",
          home_store_code: "",
          home_store_city: s.home_store_city || "",
        };
      }
    } catch { /* ignore */ }
    return { photo_url: "", last_login_at: "", has_webauthn: false, role_label: "", position: "", home_store_code: "", home_store_city: "" };
  });
  // Rotating morale ticker (User ask c).
  const [moraleItems, setMoraleItems] = useState([]);
  const [moraleIdx, setMoraleIdx] = useState(0);
  // Has-passkey hint surfaced ONLY when the device once successfully bound
  // one — saved in localStorage so we don't render the button on first launch.
  // Kept (with eslint-disable) so a future Face ID re-enable can flip the
  // UI back on without re-introducing state. UI hooks removed 2026-05-26.
  // eslint-disable-next-line no-unused-vars
  const [deviceHasPasskey, setDeviceHasPasskey] = useState(() => {
    try { return localStorage.getItem("ecDashHasPasskey") === "1"; } catch { return false; }
  });
  // eslint-disable-next-line no-unused-vars
  const [biometricBusy, setBiometricBusy] = useState(false);
  // Forced-logout reason — picked up from sessionStorage if the user
  // was just signed out by the App (session expired, maintenance,
  // district lockout, etc.). Cleared on first successful login attempt
  // OR after first render so refreshing the login page doesn't keep
  // showing the banner. (User ask 2026-06-12 "show a clear message
  // like 'Your session expired. Please sign back in.'")
  const [logoutReason, setLogoutReason] = useState(() => {
    try {
      const r = sessionStorage.getItem("ecDashLogoutReason");
      sessionStorage.removeItem("ecDashLogoutReason");
      // Suppress "session_missing_partial" for genuinely-first-time visitors
      // — no ecDashUser, no ecDashSession, and no ecDashRefresh means they
      // were never logged in, so a "signed out" banner is a lie (and it
      // was showing the raw fingerprint code `SESSION_MISSING_PARTIAL` to
      // crew, per user report 2026-08-31).
      if (r === "session_missing_partial") {
        const hasAny = ["ecDashRefresh", "ecDashUser", "ecDashSession"].some(k => {
          try { return !!localStorage.getItem(k); } catch { return false; }
        });
        if (!hasAny) return "";
      }
      return r || "";
    } catch { return ""; }
  });
  // Bug fix 2026-07-02: the reason banner never auto-dismissed and — worse
  // — was rendered outside the step="id" conditional, so it carried over
  // to the PIN screen after the user typed their ID. Fixes:
  //   1. Auto-clear after 8 s so the banner disappears on its own.
  //   2. Clear the state as soon as we transition off the ID step, so
  //      reverse-navigation (PIN → "Not you?" back to ID) inside the 8 s
  //      window can't resurrect the banner.
  //   3. Below, restrict the banner render to step === "id".
  useEffect(() => {
    if (!logoutReason) return undefined;
    const t = setTimeout(() => setLogoutReason(""), 8000);
    return () => clearTimeout(t);
  }, [logoutReason]);
  useEffect(() => {
    if (step !== "id" && logoutReason) setLogoutReason("");
  }, [step, logoutReason]);
  // After a successful PIN login on a Face-ID-capable device that doesn't
  // yet have a passkey, we stage the login completion behind a one-tap
  // "Save Face ID for next time?" panel. Critically, the `.create()` call
  // MUST happen inside the user's click handler — Safari + Chrome both
  // silently reject WebAuthn create() if it's fired from a setTimeout
  // because they require a fresh user-gesture context. The previous
  // implementation tried to enroll inside `setTimeout(800ms)` and that's
  // why no "Save passkey?" prompt ever appeared. (User report 2026-05-25.)
  // eslint-disable-next-line no-unused-vars
  const [pendingLogin, setPendingLogin] = useState(null);
  // 2026-08-27 — flipped from opt-in "keep me signed in" to opt-out
  // "sign me out at end of shift". Default = STAY signed in for 30
  // days. Only the shared-iPad case needs the short-mode toggle.
  // Storage key inverted: `ecDashSignOutMode === "1"` means user
  // opted-in to short mode.
  // 2026-06 — explicit device-type choice replaces the old
  // "sign me out at end of shift" checkbox. "personal" → 7-day session
  // with quick PIN unlock; "shared" → signed out within the hour.
  // Default assumption: every device is SHARED unless the user explicitly
  // opts into "personal" (persisted from a prior login). Shared = short
  // 1-hour session; personal = 7-day quick-PIN. (2026-09 user ask.)
  const [deviceType, setDeviceType] = useState(() => {
    try { return localStorage.getItem("ecDashDeviceType") === "personal" ? "personal" : "shared"; } catch { return "shared"; }
  });
  const isPersonal = deviceType === "personal";
  const { theme } = useTheme();
  const branding = { app_name: theme.app_name, tagline: theme.tagline };
  const isPitchBlack = (
    (theme.primary_color || "").toLowerCase() === "#ec008c" &&
    (theme.accent_color || "").toLowerCase() === "#a3e635"
  );

  // iOS standalone PWA undershoots the real screen height and leaks the
  // document's gray body background into the home-indicator strip at the
  // very bottom (user report 2026-06: "gray bar at the bottom of the login
  // screen"). Paint <body> with the login gradient's bottom color while this
  // screen is mounted so that dead-zone matches the page, then restore on
  // unmount. Lowest-risk fix — does NOT touch the global height/scroll locks.
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = isPitchBlack ? "#050505" : "#1E40AF";
    return () => { document.body.style.backgroundColor = prev; };
  }, [isPitchBlack]);

  // Track in-flight axios call so a quick double-tap doesn't double-submit.
  const submittingRef = useRef(false);
  // Synchronous mirror of `pin`. FIX 2026-09-11 (crew reported "wrong PIN
  // even though it's correct — worked on the 3rd try"): fast taps on the
  // keypad were read against the STALE render-closure `pin`, so React 18
  // batched them and a digit got dropped → a wrong 4-digit code was
  // submitted. Reading/writing this ref is synchronous, so every tap sees
  // the true current value regardless of batching. `pin` state is now
  // only used to render the dot-slots.
  const pinRef = useRef("");

  // Lightweight fetch on mount so the splash has the right store to display.
  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      setStoreInfo({
        store_number: r.data?.store_number || "3094",
        store_city: r.data?.store_city || "Wheat Ridge",
      });
    }).catch(() => {});
  }, []);

  // Reset just the PIN/error scratch state when stepping back to ID entry.
  const resetToIdStep = () => {
    setStep("id");
    pinRef.current = "";
    setPin("");
    setPinSetupFirst("");
    setPendingFirstName("");
    setEmployeeId("");
    setPendingCtx({ photo_url: "", last_login_at: "", has_webauthn: false, role_label: "", position: "", home_store_code: "", home_store_city: "" });
    setError("");
    // If we landed on the PIN screen via a soft-lock hint, tapping Back
    // means "no, that's not me" — clear the hint so we don't loop back.
    try { localStorage.removeItem("ecDashSoftLock"); } catch { /* ignore */ }
  };

  // Rotating morale ticker on the login splash. Hardcoded brand quotes
  // only — pre-login surfaces must NEVER expose store/district/email
  // counts (user report 2026-02-14: "no store data, no emails logged,
  // no district logged" on the login ticker). Earlier version pulled
  // `/api/login/feed` which mixed in real operational stats; that's now
  // gone from this surface and lives only on authenticated pages.
  useEffect(() => {
    setMoraleItems([
      "Let go and have fun.",
      "Every email is a future fan.",
    ]);
  }, []);

  useEffect(() => {
    if (moraleItems.length < 2) return undefined;
    const t = setInterval(() => setMoraleIdx((i) => (i + 1) % moraleItems.length), 4500);
    return () => clearInterval(t);
  }, [moraleItems.length]);

  // Time-of-day greeting used on the PIN screen header.
  const timeGreeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, [step]);

  // Human-readable "last seen" line. Renders only when we have a timestamp.
  const lastSeenLine = useMemo(() => {
    if (!pendingCtx.last_login_at) return "";
    try {
      const d = new Date(pendingCtx.last_login_at);
      const now = new Date();
      const diffH = (now - d) / 3600000;
      if (diffH < 1) return "Welcome back!";
      if (diffH < 18) {
        return `Last seen ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
      }
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const that = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const days = Math.round((today - that) / 86400000);
      if (days === 1) return "Last seen yesterday";
      if (days < 7) return `Last seen ${d.toLocaleDateString(undefined, { weekday: "long" })}`;
      return `Last seen ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    } catch { return ""; }
  }, [pendingCtx.last_login_at]);

  // ---- Face ID / passkey (WebAuthn) -------------------------------------
  // Tiny helpers — base64url <→ ArrayBuffer + recursive coerce for the
  // PublicKeyCredentialCreationOptions / RequestOptions JSON the server
  // sends back. Browsers expect ArrayBuffers in specific spots.
  const _b64ToBuf = (b64) => {
    const s = b64.replace(/-/g, "+").replace(/_/g, "/");
    const pad = s + "=".repeat((4 - (s.length % 4)) % 4);
    const bin = atob(pad);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return buf.buffer;
  };
  const _bufToB64Url = (buf) => {
    const bytes = new Uint8Array(buf);
    let s = "";
    for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  };
  const _coerceCreate = (opts) => ({
    ...opts,
    challenge: _b64ToBuf(opts.challenge),
    user: { ...opts.user, id: _b64ToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({ ...c, id: _b64ToBuf(c.id) })),
  });
  const _coerceGet = (opts) => ({
    ...opts,
    challenge: _b64ToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({ ...c, id: _b64ToBuf(c.id) })),
  });
  const _serializeCred = (cred) => {
    const r = cred.response;
    const out = {
      id: cred.id,
      rawId: _bufToB64Url(cred.rawId),
      type: cred.type,
      authenticatorAttachment: cred.authenticatorAttachment,
      response: {
        clientDataJSON: _bufToB64Url(r.clientDataJSON),
      },
    };
    if (r.attestationObject) out.response.attestationObject = _bufToB64Url(r.attestationObject);
    if (r.authenticatorData) out.response.authenticatorData = _bufToB64Url(r.authenticatorData);
    if (r.signature) out.response.signature = _bufToB64Url(r.signature);
    if (r.userHandle) out.response.userHandle = _bufToB64Url(r.userHandle);
    if (typeof r.getTransports === "function") out.response.transports = r.getTransports();
    return out;
  };

  const webauthnSupported = typeof window !== "undefined" && window.PublicKeyCredential && navigator.credentials;

  // "Sign in with Face ID" — uses discoverable credentials so the user
  // doesn't even need to type their Employee ID. The browser picks the
  // passkey, Face ID/Touch ID prompts, server verifies the signature.
  const signInWithBiometric = async () => {
    if (!webauthnSupported) { setError("Face ID not supported on this device"); return; }
    setBiometricBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/webauthn/options/login`);
      const opts = JSON.parse(data.options);
      const cred = await navigator.credentials.get({ publicKey: _coerceGet(opts) });
      if (!cred) throw new Error("Cancelled");
      const verify = await axios.post(`${API}/auth/webauthn/login`, { response: _serializeCred(cred) });
      if (verify.data?.crew_id) {
        try { localStorage.setItem("ecDashHasPasskey", "1"); } catch (_) {}
        setLoggedInUser(verify.data);
        onLogin(verify.data);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Face ID sign-in failed";
      setError(typeof detail === "string" ? detail : "Face ID sign-in failed");
    } finally {
      setBiometricBusy(false);
    }
  };

  // After a successful PIN login, offer to bind a passkey to this device
  // so subsequent launches are one-tap. MUST run inside a click handler
  // — both Safari and Chrome reject `navigator.credentials.create()` if
  // the call isn't inside a fresh user-gesture context. (User report
  // 2026-05-25.)
  const enrollBiometric = async (user) => {
    if (!webauthnSupported || !user?.crew_id) {
      console.warn("[passkey] enroll skipped — supported:", webauthnSupported, "user:", !!user?.crew_id);
      return false;
    }
    console.log("[passkey] enroll start for", user.crew_id);
    try {
      const { data } = await axios.post(
        `${API}/auth/webauthn/options/register`, { crew_id: user.crew_id },
      );
      const opts = JSON.parse(data.options);
      console.log("[passkey] got options, calling navigator.credentials.create()");
      const cred = await navigator.credentials.create({ publicKey: _coerceCreate(opts) });
      if (!cred) {
        console.warn("[passkey] credentials.create() returned null — user cancelled?");
        return false;
      }
      console.log("[passkey] cred created, verifying with server…");
      await axios.post(
        `${API}/auth/webauthn/register`,
        { crew_id: user.crew_id, response: _serializeCred(cred) },
      );
      try { localStorage.setItem("ecDashHasPasskey", "1"); } catch (_) {}
      setDeviceHasPasskey(true);
      console.log("[passkey] enroll success");
      return true;
    } catch (e) {
      console.warn("[passkey] enroll failed:", e?.name, e?.message, e?.response?.data);
      return false;
    }
  };


  // ---- Step 1: Employee ID lookup ---------------------------------------
  const handleSubmitId = async () => {
    if (!employeeId.trim()) { setError("Enter your Employee ID"); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/crew-login`, { employee_id: employeeId.trim() }, { timeout: 15000 });
      setPendingFirstName(res.data.first_name || "");
      setPendingCtx({
        photo_url: res.data.photo_url || "",
        last_login_at: res.data.last_login_at || "",
        has_webauthn: !!res.data.has_webauthn,
        role_label: res.data.role_label || "",
        position: res.data.position || "",
        home_store_code: res.data.home_store_code || "",
        home_store_city: res.data.home_store_city || "",
      });
      setStep(res.data.pin_setup_required ? "pin-setup" : "pin");
      pinRef.current = "";
      setPin("");
      setPinSetupFirst("");
    } catch (e) {
      const status = e.response?.status;
      const timedOut = e.code === "ECONNABORTED" || /timeout/i.test(e.message || "");
      if (status === 503) {
        // Lockout (maintenance_mode or district_lockout). The amber
        // banner above the form already explains "the app is getting
        // some updates" — surfacing the same copy in the red error
        // box was a confusing double-message. Use a shorter, action-
        // focused line so the two stripes complement each other
        // instead of repeating. (User report 2026-05-28 with screenshot
        // showing the duplicate text.)
        setError("Logins are paused right now. Try again later.");
      } else if (timedOut) {
        setError("Connection's slow — tap Continue again.");
      } else {
        setError(e.response?.data?.detail || "Employee ID not recognized");
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  // ---- Step 2a: existing PIN verify -------------------------------------
  const submitExistingPin = async (fullPin) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/crew-pin`, {
        employee_id: employeeId.trim(),
        pin: fullPin,
        device_type: deviceType,
      }, { timeout: 15000 });
      finalizeLogin(res.data);
    } catch (e) {
      // Network stalled on store wifi — the request never resolved, which
      // used to leave `loading` (and the disabled keypad) stuck forever,
      // i.e. "pin pad locked on a number" (crew report 2026-09-11). The
      // 15s timeout above now rejects here so `finally` clears it. Clear
      // the pad and give a retry-able message.
      const timedOut = e.code === "ECONNABORTED" || /timeout/i.test(e.message || "");
      // 409 → server now thinks no PIN is set (rare race after manager reset).
      // Bounce them to setup mode rather than spinning here.
      if (e.response?.status === 409) {
        setStep("pin-setup");
        pinRef.current = "";
        setPin("");
        setPinSetupFirst("");
        setError("");
      } else if (e.response?.status === 503) {
        // Lockout — short, action-focused copy (see ID-step catch).
        pinRef.current = "";
        setPin("");
        setError("Logins are paused right now. Try again later.");
      } else if (timedOut) {
        pinRef.current = "";
        setPin("");
        setError("Connection's slow — tap your PIN again.");
      } else {
        pinRef.current = "";
        setPin("");
        setError(e.response?.data?.detail || "Wrong PIN — try again.");
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  // ---- Step 2b: first-time PIN setup ------------------------------------
  const submitNewPinSetup = async (confirmedPin) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/crew-pin-setup`, {
        employee_id: employeeId.trim(),
        pin: confirmedPin,
        device_type: deviceType,
      }, { timeout: 15000 });
      finalizeLogin(res.data);
    } catch (e) {
      // Server rejected (e.g. obvious "0000"/"1234"). Send them back to first
      // entry with the message — keeps the flow on one screen.
      setStep("pin-setup");
      setPinSetupFirst("");
      pinRef.current = "";
      setPin("");
      setError(e.response?.data?.detail || "Couldn't set PIN. Try a less guessable one.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  // Common post-success: persist the session, hand off to HomePage with splash.
  const finalizeLogin = (userPayload) => {
    try {
      localStorage.setItem("ecDashUser", JSON.stringify(userPayload));
      sessionStorage.removeItem("ecDashUser");
      localStorage.setItem("ecDashSession", JSON.stringify({
        loginAt: Date.now(),
        // 2026-06 — device-type drives session length: personal = 7 days
        // (quick PIN unlock), shared = 1 hour (full re-login).
        deviceType: deviceType,
        stayLoggedIn: isPersonal,
        // Mark this session as having gone through the 4-digit PIN flow.
        // App.js boot-up rejects any saved session without this flag so
        // pre-PIN sessions (everyone logged in before v2.0.30) are forced
        // back to the login screen → first-time PIN setup. (security
        // migration 2026-02-22)
        authMethod: "pin",
      }));
      localStorage.setItem("ecDashDeviceType", deviceType);
      // Silent-refresh token (2026-08-31). Stored separately from the
      // session so an expired ecDashSession doesn't nuke the refresh
      // ability. TTL is baked into the token server-side (90d ticked,
      // 7d unticked) — the client never has to reason about it.
      if (userPayload?.refresh_token) {
        localStorage.setItem("ecDashRefresh", userPayload.refresh_token);
      }
      // Soft-lock recovery consumed — remove the hint so a future normal
      // login doesn't get stuck on this crew's PIN screen.
      localStorage.removeItem("ecDashSoftLock");
    } catch (e) { /* ignore */ }
    // Face ID enrollment offer removed 2026-05-26 (button was non-functional
    // for some users). Always hand the user straight to the post-login
    // splash → HomePage flow now. WebAuthn helpers + endpoints remain in
    // place for a future re-enable.
    // SECURITY (2026-06-16): if AppRouter redirected an unauthenticated
    // deep-link visitor here (e.g. someone tapped a shared print URL),
    // honor the saved destination so the user lands back on the page
    // they were trying to reach — but ONLY after a real sign-in (the
    // `postLoginRedirect` cookie is consumed and cleared so a future
    // bare LoginPage open doesn't accidentally re-trigger it).
    try {
      const dest = localStorage.getItem("postLoginRedirect");
      if (dest && dest.startsWith("/")) {
        localStorage.removeItem("postLoginRedirect");
        window.location.assign(dest);
        return;
      }
    } catch { /* ignore */ }
    setLoggedInUser(userPayload);
    onLogin(userPayload);
  };

  // "Save Face ID for next time?" panel handlers. Stays inside the user-
  // gesture chain so Safari accepts the WebAuthn registration.
  const acceptPasskey = async () => {
    if (!pendingLogin) return;
    setBiometricBusy(true);
    await enrollBiometric(pendingLogin);
    setBiometricBusy(false);
    const u = pendingLogin;
    setPendingLogin(null);
    setLoggedInUser(u);
    onLogin(u);
  };

  const skipPasskey = () => {
    const u = pendingLogin;
    setPendingLogin(null);
    if (u) { setLoggedInUser(u); onLogin(u); }
  };

  // ---- Keypad handlers (drive whichever PIN field is active) ------------
  const onPinDigit = (d) => {
    setError("");
    const current = pinRef.current;
    if (current.length >= 4) return;
    const next = current + d;
    pinRef.current = next;
    setPin(next);
    if (next.length === 4) {
      // Auto-submit when 4 digits filled.
      if (step === "pin") {
        submitExistingPin(next);
      } else if (step === "pin-setup") {
        // First pass complete — move to confirm.
        setPinSetupFirst(next);
        pinRef.current = "";
        setPin("");
        setStep("pin-confirm");
      } else if (step === "pin-confirm") {
        if (next !== pinSetupFirst) {
          pinRef.current = "";
          setPin("");
          setError("PINs don't match. Try again.");
          // Send them back to first-pass setup so they re-enter both.
          setPinSetupFirst("");
          setStep("pin-setup");
        } else {
          submitNewPinSetup(next);
        }
      }
    }
  };
  const onPinBackspace = () => {
    setError("");
    const next = pinRef.current.slice(0, -1);
    pinRef.current = next;
    setPin(next);
  };

  // ---- Hardware keyboard support on the PIN screen ----------------------
  // Crew on a desktop / Mac (or PWA running in a browser tab) often want to
  // type their PIN on the physical keyboard rather than tap the on-screen
  // keypad. Listen at the window level whenever any PIN step is active and
  // forward 0-9 / Backspace / Enter to the same handlers the keypad uses.
  // Ignored when focus is in an actual <input> (defensive — there's no
  // input on the PIN card today, but Step 1's Employee ID input shouldn't
  // get hijacked if state ever overlaps).
  const isPinStep = step === "pin" || step === "pin-setup" || step === "pin-confirm";
  useEffect(() => {
    if (!isPinStep) return;
    const handler = (e) => {
      // Skip if user is typing in an actual editable field.
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target && e.target.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (loading) return;
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        onPinDigit(e.key);
      } else if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        onPinBackspace();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // onPinDigit/onPinBackspace are recreated each render but they read the
    // latest `pin` / `step` via closure in this same render, so we re-bind
    // every render. Cheap (single listener) and avoids stale-closure bugs
    // where typing the 4th digit wouldn't auto-submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  if (loggedInUser) {
    const pickToInitial = {
      tally: { role: "crew" },
      manager: { role: "manager" },
      inbox: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "inbox" },
      // DMs are role="manager" already; admins need the flip. (2026-05-22 —
      // Reports tile now visible to DMs too, so the handoff must accept
      // is_district_manager as a valid "manager-role" gate.)
      reports: { role: (loggedInUser.is_manager || loggedInUser.is_admin || loggedInUser.is_district_manager) ? "manager" : "crew", initialView: "reports" },
      settings: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "settings" },
      profile: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "profile" },
      team: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "team" },
      leaderboard: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "leaderboard" },
      contest: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "contest" },
      balloons: { role: loggedInUser.is_manager ? "manager" : "crew" },  // legacy route — campaign cleanup 2026-05-19, falls through to home
      // 2026-05-05 — Calendar tile bypass kept so first post-login Calendar
      // tap routes directly to that page instead of CrewTallyPage.
      calendar: { role: loggedInUser.is_manager ? "manager" : "crew", initialView: "calendar" },
      dumpling: { role: loggedInUser.is_manager ? "manager" : "crew" },  // legacy route — campaign cleanup 2026-05-19, falls through to home
      // 2026-05-19 — Admin page is admin-only. Set role to "manager" so
      // the LoginPage handoff doesn't dump them into the bare CrewTallyPage
      // before App.handleLogin can route them to the new AdminPage.
      admin: { role: loggedInUser.is_admin ? "manager" : "crew", initialView: "admin" },
      // 2026-05-19 — District Manager dashboard. Role flipped to "manager"
      // for admins (so the LoginPage handoff doesn't bounce them into the
      // bare CrewTallyPage). DMs already have role=manager from auth.
      district: { role: (loggedInUser.is_admin || loggedInUser.is_district_manager) ? "manager" : "crew", initialView: "district" },
    };
    return (
      <>
        {!splashDone && (
          <StoreSplash
            logoUrl={theme.logo_url}
            storeNumber={loggedInUser.home_store_code || storeInfo.store_number}
            storeCity={loggedInUser.home_store_city || storeInfo.store_city}
            firstName={loggedInUser.first_name}
            onDone={() => setSplashDone(true)}
          />
        )}
        <HomePage
          user={loggedInUser}
          crew={crew}
          onPick={(key) => onLogin({ ...loggedInUser, ...pickToInitial[key] })}
          onSwitchUser={() => {
            // Full logout cleanup — mirror App.handleLogout so the two logout
            // paths are identical. Wipes storage, clears caches, unregisters
            // the SW, fires the server beacon, then resets local state.
            try {
              ["ecDashUser", "ecDashSession", "ecDashCrew", "ecDashForceReloadAt"].forEach((k) => {
                try { localStorage.removeItem(k); } catch { /* ignore */ }
                try { sessionStorage.removeItem(k); } catch { /* ignore */ }
              });
              try { sessionStorage.clear(); } catch { /* ignore */ }
              if (typeof caches !== "undefined" && caches.keys) {
                caches.keys().then((names) => names.forEach((n) => caches.delete(n))).catch(() => {});
              }
              if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
                navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister())).catch(() => {});
              }
            } catch { /* ignore */ }
            try {
              const url = `${API}/auth/logout`;
              const body = JSON.stringify({ crew_id: loggedInUser?.crew_id });
              if (navigator.sendBeacon) {
                navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
              } else {
                fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
              }
            } catch { /* best effort */ }
            setLoggedInUser(null);
            setEmployeeId("");
            resetToIdStep();
            setSplashDone(false);
          }}
        />
      </>
    );
  }

  // Header text + sub-copy for whichever PIN screen is active. Keeping this
  // centralized keeps the visual structure of the card identical across
  // steps so it slides in/out without layout shifts.
  const pinHeader = step === "pin"
    ? { kicker: `Hey, ${pendingFirstName || ""}`, title: "Enter your PIN", sub: "4 digits to keep your account safe." }
    : step === "pin-setup"
      ? { kicker: `Welcome, ${pendingFirstName || ""}`, title: "Create your PIN", sub: "Pick 4 digits — you'll use these every time you sign in." }
      : { kicker: `One more time, ${pendingFirstName || ""}`, title: "Confirm your PIN", sub: "Re-enter the 4 digits to lock it in." };

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden"
      style={{
        height: "100dvh",
        background: isPitchBlack
          ? "linear-gradient(160deg, #050505 0%, #14001a 50%, #050505 100%)"
          : "linear-gradient(160deg, var(--fb-blue-dark) 0%, var(--fb-blue) 45%, #1E40AF 100%)",
      }}
      data-testid="login-page"
    >
      {/* Pitch Black event artwork — faded poster backdrop behind the
          login card. Only on the Pitch Black theme. */}
      {isPitchBlack && (
        <>
          <div
            className="absolute inset-0 pointer-events-none bg-center bg-cover"
            style={{ backgroundImage: "url(/pitch-black-poster.webp)", opacity: 0.28 }}
            aria-hidden
            data-testid="login-pitch-black-art"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.85) 85%)" }}
            aria-hidden
          />
        </>
      )}
      {/* Animated drifting orbs — 2026-05-25 a. */}
      <div className="login-bg-orbs" aria-hidden />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }}
        aria-hidden
      />

      <div className="relative flex-1 flex flex-col items-center justify-center px-5 py-4 sm:py-8 overflow-y-auto">
        {/* Forced-logout banner — surfaces a human-readable reason when
            the user was just signed out by the app (session expired,
            maintenance, etc.). Auto-dismisses after 8 s (see useEffect
            above) and only renders on the ID entry step so it never
            follows the user onto the PIN screen. */}
        {logoutReason && step === "id" && (
          <div
            className="w-full max-w-sm mb-3 rounded-xl px-4 py-2.5 bg-amber-100/95 text-amber-900 text-[12px] font-bold shadow-lg border border-amber-300"
            data-testid="login-logout-reason"
            onClick={() => setLogoutReason("")}
          >
            {(() => {
              switch (logoutReason) {
                case "session_expired_shared":
                case "session_expired_shared_poll":
                  return "This shared device signed you out. Please sign back in.";
                case "session_expired_personal":
                case "session_expired_personal_poll":
                  return "Welcome back — enter your PIN to pick up where you left off.";
                case "session_expired_8h":
                case "session_expired_8h_poll":
                  return "Your session ended. Please sign back in.";
                case "session_malformed":
                case "user_malformed":
                  return "We couldn't verify your session. Please sign in again.";
                case "session_missing_partial":
                  return "Please sign in to continue.";
                case "pin_migration_cutoff":
                  return "One-time update — please sign in again to set up your PIN.";
                case "store_maintenance_on":
                  return "Your store paused the app for maintenance. Sign in once it's back up.";
                case "district_locked_out":
                  return "The district has temporarily locked sign-ins. Try again in a few minutes.";
                case "midnight_refresh":
                  return "Daily 12 AM refresh — sign back in to continue.";
                case "manual":
                  return "Signed out. See you next shift!";
                default:
                  // Never leak raw fingerprint codes to crew (user report
                  // 2026-08-31 "what's SESSION_MISSING_PARTIAL?").
                  return "Please sign in to continue.";
              }
            })()}
          </div>
        )}
        {step === "id" && (
        <div className="w-full max-w-sm text-center mb-6 fb-enter" style={{ "--fb-delay": "0ms" }}>
          <div className="inline-block relative">
            <img
              src={theme.logo_url}
              alt={theme.app_name}
              className="w-24 h-24 rounded-2xl mx-auto object-cover shadow-2xl ring-4 ring-white/20"
            />
          </div>
          <h1
            className="mt-5 text-4xl sm:text-5xl font-black tracking-tight text-white leading-[0.95]"
            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.02em" }}
            data-testid="login-app-name"
          >
            {branding.app_name || "Wow App"}
          </h1>
          {/* Static tagline removed 2026-05-26 — the rotating morale ticker
              pill below already carries the inspirational copy and the
              two surfaces stacked felt redundant. (User ask: "since you
              have the banners in the pills changing lets remove the let
              go and have fun under the wow app".) */}

          {/* Rotating morale ticker — only on ID step so the auth screen
              stays focused once the user starts typing. Soft chip, mounts
              with a fade so the rotation doesn't feel jumpy. (User ask c.) */}
          {step === "id" && moraleItems.length > 0 && (
            <div
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15"
              data-testid="login-morale-ticker"
            >
              <Sparkle weight="fill" className="w-3 h-3 text-amber-300 flex-shrink-0" />
              <span
                key={moraleIdx}
                className="text-[11px] sm:text-xs font-semibold text-white/95 fb-enter"
                style={{ "--fb-delay": "0ms" }}
              >
                {moraleItems[moraleIdx]}
              </span>
            </div>
          )}
        </div>
        )}

        <div
          className="w-full max-w-sm fb-enter"
          style={{ "--fb-delay": "120ms" }}
        >
          {/* Maintenance banner — surfaced above the auth card so non-admin
              crew immediately understand why their login may be rejected,
              and admins know they CAN still sign in. (User report 2026-05-18.)
              2026-06-15: Banner is now FULLY admin-controlled. Renders only
              if admin has set a non-empty `pause_banner_message`. If the
              admin clears the message in Settings → Maintenance Mode, the
              banner disappears entirely from the login screen — no
              hardcoded fallback copy. (User ask: "make the updates banner
              fully editable and removable".)
              Still hidden on the PIN entry steps (`step !== "id"`) to keep
              that view focused. */}
          {maintenanceOn && step === "id" && pauseBannerMessage.trim() !== "" && (
            <div
              className="mb-4 rounded-2xl px-4 py-3 flex items-start gap-2.5 backdrop-blur-md"
              style={{
                background: "rgba(254, 243, 199, 0.92)",
                border: "1px solid rgba(217, 119, 6, 0.35)",
              }}
              data-testid="login-maintenance-banner"
            >
              <span className="text-base flex-shrink-0" aria-hidden>🛠️</span>
              <div className="text-left">
                <p className="text-xs font-black uppercase tracking-wider text-amber-900">Heads up</p>
                <p className="text-[11px] text-amber-800 leading-snug mt-0.5 whitespace-pre-line" data-testid="login-maintenance-banner-message">
                  {pauseBannerMessage.trim()}
                </p>
              </div>
            </div>
          )}
          <div
            className="rounded-3xl p-4 sm:p-6 shadow-2xl"
            style={{
              backgroundColor: isPitchBlack ? "rgba(12,10,16,0.55)" : "rgba(255,255,255,0.96)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: isPitchBlack ? "1px solid rgba(236,0,140,0.45)" : "1px solid rgba(255,255,255,0.4)",
              boxShadow: isPitchBlack
                ? "0 10px 40px rgba(0,0,0,0.7), 0 0 28px rgba(236,0,140,0.25), inset 0 1px 1px rgba(255,255,255,0.12)"
                : undefined,
            }}
            data-testid={step === "id" ? "login-card-id" : "login-card-pin"}
          >
            {step === "id" ? (
              // ============================================================
              // STEP 1 — Employee ID entry (unchanged from pre-PIN behavior)
              // ============================================================
              <>
                <label className="block text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Employee ID
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value.replace(/\D/g, ""))}
                  placeholder="00000000"
                  className="w-full text-center text-3xl font-black tracking-[0.3em] h-16 rounded-2xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                  // Force dark text + white bg on the input regardless of
                  // the active theme. Some themes (e.g. Spider-Man's
                  // dark preset) were making the typed digits invisible
                  // by inheriting a light text color from the theme's
                  // global text_color CSS variable. Locking the input
                  // colors keeps it legible under every preset.
                  style={{
                    fontFamily: "'Five Below Gothic', Chivo, sans-serif",
                    color: "#0F172A",
                    backgroundColor: "#FFFFFF",
                    caretColor: "#0F172A",
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmitId()}
                  autoFocus
                  data-testid="employee-id-input"
                />

                {error && (
                  <div
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium"
                    data-testid="login-error"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-2" data-testid="device-type-chooser">
                  <span className="text-[11px] font-semibold text-slate-500">This device is</span>
                  <div className="inline-flex rounded-full bg-slate-100 p-0.5">
                    <button
                      type="button"
                      onClick={() => setDeviceType("personal")}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all ${isPersonal ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`}
                      data-testid="device-type-personal"
                      aria-pressed={isPersonal}
                    >
                      Personal
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeviceType("shared")}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all ${!isPersonal ? "bg-white text-amber-700 shadow-sm" : "text-slate-500"}`}
                      data-testid="device-type-shared"
                      aria-pressed={!isPersonal}
                    >
                      Shared
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-[10px] text-slate-400 text-right" data-testid="device-type-hint">
                  {isPersonal ? "Stays 7 days · quick PIN unlock" : "Signs out within the hour"}
                </p>

                <Button
                  onClick={handleSubmitId}
                  disabled={loading || !employeeId.trim()}
                  className="w-full mt-4 h-14 text-base font-black rounded-2xl shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
                  style={{
                    backgroundColor: "var(--fb-blue)",
                    color: "#FFFFFF",
                    fontFamily: "'Five Below Gothic', Chivo, sans-serif",
                    letterSpacing: "0.05em",
                  }}
                  data-testid="login-button"
                >
                  {loading ? (
                    "CHECKING…"
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      CONTINUE <ArrowRight weight="bold" className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                {/* "Sign in with Face ID" button removed 2026-05-26 per
                    user ask "Face ID doesn't do anything after you click on
                    it so remove it". WebAuthn helpers + backend endpoints
                    remain intact for a future re-enable. */}
              </>
            ) : (
              // ============================================================
              // STEP 2 — PIN entry (covers verify, setup, and confirm)
              // ============================================================
              <div className="fb-enter" style={{ "--fb-delay": "0ms" }}>
                {/* Hero header — gradient band + overlapping avatar + name
                    + role line. Mirrors the founder/profile card look so
                    the auth screen feels like part of the same product.
                    (User ask 2026-05-25 with reference image of the
                    desired layout — avatar centered on top of a gradient
                    header, big name underneath, role+store as a subline.) */}
                {step === "pin" && (
                  <div className="-mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-3 sm:mb-4">
                    <div className="relative">
                      <div
                        className="h-16 sm:h-20 w-full rounded-t-3xl"
                        style={{
                          background:
                            "linear-gradient(135deg, var(--fb-blue, #0048B3) 0%, #3B82F6 60%, #60A5FA 100%)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={resetToIdStep}
                        className="absolute top-2 left-2 px-2.5 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center gap-1 text-white text-[10px] font-bold uppercase tracking-wider transition-colors"
                        data-testid="pin-back-button"
                        aria-label="Use a different ID"
                      >
                        <ArrowLeft weight="bold" className="w-3 h-3" /> Not you?
                      </button>
                      {/* Avatar absolutely overlaps the band. Sized down on
                          tight Zebra screens so it doesn't dominate the
                          card. */}
                      <div className="absolute left-1/2 -translate-x-1/2 top-8 sm:top-10 z-[5] rounded-full bg-white shadow-lg" style={{ padding: 4 }}>
                        {pendingCtx.photo_url ? (
                          <img
                            src={pendingCtx.photo_url}
                            alt={pendingFirstName}
                            className="block w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover object-top"
                            data-testid="pin-avatar"
                          />
                        ) : (
                          <div
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-white text-2xl sm:text-3xl font-black"
                            style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                            data-testid="pin-avatar-fallback"
                          >
                            {(pendingFirstName || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="px-4 sm:px-6 pt-11 sm:pt-14 text-center">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1">
                        {timeGreeting}
                      </p>
                      <h3
                        className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight"
                        style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
                        data-testid="pin-greeting-name"
                      >
                        {pendingFirstName}
                      </h3>
                      <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 mt-1" data-testid="pin-role-line">
                        {pendingCtx.role_label || "Wow Crew"}
                      </p>
                      {pendingCtx.home_store_code && (
                        <p className="text-[11px] font-semibold text-slate-700 mt-1.5" data-testid="pin-store-line">
                          #{pendingCtx.home_store_code}
                          {pendingCtx.home_store_city ? ` ${pendingCtx.home_store_city}` : ""}
                        </p>
                      )}
                      {lastSeenLine && (
                        <p className="text-[11px] text-slate-400 mt-1.5">{lastSeenLine}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Setup flow keeps the simple back button — no hero band. */}
                {step !== "pin" && (
                  <button
                    type="button"
                    onClick={resetToIdStep}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2"
                    data-testid="pin-back-button-setup"
                  >
                    <ArrowLeft weight="bold" className="w-3.5 h-3.5" /> Use a different ID
                  </button>
                )}

                {/* "Hey, {firstName}" kicker only on PIN-setup step. On the
                    regular PIN step the hero card above already says
                    "Good evening · Dom" so this would be a duplicate.
                    (User feedback 2026-05-25 "delete that second hey dom".) */}
                {step !== "pin" && (
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 text-center">
                    {pinHeader.kicker}
                  </p>
                )}
                <h3
                  className="text-xl font-bold tracking-tight text-slate-800 mt-0.5 text-center"
                  style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                  data-testid="pin-title"
                >
                  {pinHeader.title}
                </h3>
                <p className="text-xs text-slate-500 mt-1 mb-4 text-center">{pinHeader.sub}</p>

                <PinPad length={4} value={pin} onChange={() => {}} />

                {error && (
                  <div
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium"
                    data-testid="pin-error"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Keypad
                  onDigit={onPinDigit}
                  onBackspace={onPinBackspace}
                  disabled={loading}
                />

                {/* "Use Face ID" PIN-step button removed 2026-05-26 per
                    user ask "Face ID doesn't do anything after you click
                    on it so remove it". WebAuthn helpers + backend
                    endpoints remain intact for a future re-enable. */}
                {/* 2026-06-12: replaced the keyboard tip with a Forgot
                    PIN? trigger — wow crew on the floor were forgetting
                    PINs and had no recovery path that didn't involve
                    physically finding their SM. Now: tap → request goes
                    to all SMs at their store (push + email to SM with
                    notification_email). Manager Approve → PIN cleared,
                    crew gets the PIN-setup screen. Manager Deny → "see
                    your SM in person". */}
                {step === "pin" && (
                  <ForgotPinTrigger
                    employeeId={employeeId}
                    onApproved={() => { setStep("pin-setup"); pinRef.current = ""; setPin(""); setError(""); }}
                  />
                )}
                {loading && (
                  <p className="mt-3 text-center text-xs text-slate-500" data-testid="pin-loading">
                    {step === "pin" ? "Signing in…" : "Saving your PIN…"}
                  </p>
                )}
              </div>
            )}
          </div>

          <p
            className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60"
            data-testid="app-version"
          >
            v{APP_VERSION}
          </p>

          {/* Install-on-phone CTA — centered directly under the version
              number for a tidy, modern stack. `autoShow` also pops the
              modal automatically the FIRST time a phone visitor lands here
              so the install path is impossible to miss (one-time per
              device, dismissible). Renders nothing once the app is running
              as a PWA. (User ask 2026-05-21.) */}
          <div className="mt-2 flex justify-center">
            <InstallAppPrompt variant="button" autoShow />
          </div>
        </div>
      </div>

      {/* "Save Face ID for next time?" offer panel removed 2026-05-26 —
          the Face ID UX wasn't reliable for some users so we pulled the
          surface area entirely. WebAuthn helpers + backend endpoints
          remain intact for a future re-enable. */}
    </div>
  );
}

/**
 * ForgotPinTrigger — small "Forgot PIN?" link under the PIN dots.
 * Tap → POST /auth/pin-reset/request with the entered employee_id, then
 * swap the link out for a polling status surface:
 *   - pending  → "Waiting for your manager to approve… ({mm:ss left})"
 *   - approved → fires onApproved() so LoginPage routes to PIN setup
 *   - denied   → "Denied by {name}. See your SM in person."
 *   - expired  → "Request expired. Tap to try again."
 *
 * The request_id lives in `sessionStorage` so a brief tab close/reopen
 * doesn't lose the in-flight request.
 */
function ForgotPinTrigger({ employeeId, onApproved }) {
  const STORAGE_KEY = `wow_forgot_pin_${employeeId}`;
  const [reqId, setReqId] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [status, setStatus] = useState("idle");
  const [actedBy, setActedBy] = useState("");
  const [denyReason, setDenyReason] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Tick every second for the countdown.
  useEffect(() => {
    if (status !== "pending") return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [status]);

  // Poll request status every 3s while pending.
  useEffect(() => {
    if (!reqId || status !== "pending") return undefined;
    let cancelled = false;
    const poll = async () => {
      // Pause polling while the tab is backgrounded — no point burning
      // battery hitting the API when the manager can't see the result.
      // (User feedback 2026-08-01 re: iPhone battery.)
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await axios.get(`${API}/auth/pin-reset/request/${reqId}`);
        if (cancelled) return;
        setStatus(r.data?.status || "pending");
        setActedBy(r.data?.acted_by_name || "");
        setDenyReason(r.data?.deny_reason || "");
        setExpiresAt(r.data?.expires_at || null);
        if (r.data?.status === "approved") {
          try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
          // small delay so the user sees the "Approved!" flash
          setTimeout(() => onApproved && onApproved(), 800);
        }
      } catch { /* swallow — next tick retries */ }
    };
    poll(); // immediate
    const t = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, [reqId, status, onApproved, STORAGE_KEY]);

  const submit = async () => {
    if (submitting || !employeeId) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/pin-reset/request`, { employee_id: employeeId });
      const id = r.data?.request_id;
      if (id) {
        try { sessionStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
        setReqId(id);
        setStatus("pending");
        setExpiresAt(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send request — try again";
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Time remaining for pending requests
  let countdown = "";
  if (status === "pending" && expiresAt) {
    const ms = new Date(expiresAt).getTime() - now;
    if (ms > 0) {
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      countdown = `${m}m ${s.toString().padStart(2, "0")}s left`;
    }
  }

  // Idle → "Forgot PIN?" link
  if (status === "idle" && !reqId) {
    return (
      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        className="mt-3 mx-auto block text-center text-xs font-bold text-blue-600 hover:text-blue-700 active:text-blue-800 underline-offset-2 hover:underline disabled:opacity-50 transition-colors"
        data-testid="forgot-pin-trigger"
      >
        {submitting ? "Sending request…" : "Forgot PIN? Ask your manager to reset"}
      </button>
    );
  }

  if (status === "approved") {
    return (
      <p
        className="mt-3 mx-auto text-center text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5 inline-block"
        data-testid="forgot-pin-approved"
      >
        ✓ Approved{actedBy ? ` by ${actedBy}` : ""} — set a new PIN below
      </p>
    );
  }

  if (status === "denied") {
    return (
      <div className="mt-3 mx-auto max-w-xs text-center" data-testid="forgot-pin-denied">
        <p className="text-xs font-bold text-rose-700 bg-rose-50 rounded-2xl px-3 py-2">
          Denied{actedBy ? ` by ${actedBy}` : ""}.<br />
          See your store manager in person to reset.
        </p>
        {denyReason && (
          <p className="text-[10px] text-slate-500 mt-1.5 italic">"{denyReason}"</p>
        )}
      </div>
    );
  }

  if (status === "expired") {
    return (
      <button
        type="button"
        onClick={() => { setReqId(null); setStatus("idle"); try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ } }}
        className="mt-3 mx-auto block text-center text-xs font-bold text-slate-600 hover:text-slate-800 underline"
        data-testid="forgot-pin-expired"
      >
        Request expired · tap to try again
      </button>
    );
  }

  // Pending
  return (
    <div className="mt-3 mx-auto text-center" data-testid="forgot-pin-pending">
      <p className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-50 rounded-full px-3 py-1.5">
        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        Waiting for manager approval…
      </p>
      {countdown && (
        <p className="text-[10px] text-slate-500 mt-1">{countdown}</p>
      )}
    </div>
  );
}

